<template>
  <v-app class="design-gallery-app">
    <menu-bar />

    <v-main class="design-gallery">
      <header class="design-gallery__header">
        <img :src="logo" alt="BOX21" class="design-gallery__logo" />
        <h1>Front page design variations</h1>
        <p>
          Ten layout explorations built around the BOX21 logo and brand colors
          (<code>#0677b9</code> / <code>#7bacd6</code>).
          Pick a card to preview full-screen.
        </p>
      </header>

      <div class="design-gallery__grid">
        <router-link
          v-for="variant in variants"
          :key="variant.id"
          :to="`/designs/${variant.id}`"
          class="design-card"
        >
          <div :class="['design-card__preview', `design-card__preview--${variant.id}`]">
            <img :src="logo" alt="" class="design-card__mini-logo" />
          </div>
          <div class="design-card__body">
            <h2>{{ variant.name }}</h2>
            <p>{{ variant.subtitle }}</p>
            <span v-if="variant.inspiration !== 'Original'" class="design-card__inspiration">
              Inspired by {{ variant.inspiration }}
            </span>
          </div>
        </router-link>
      </div>

      <p class="design-gallery__back">
        <router-link to="/">← Current production front page</router-link>
      </p>
    </v-main>
  </v-app>
</template>

<script>
import MenuBar from '@/components/layout/MenuBar.vue'
import { BRAND, DESIGN_VARIANTS } from '@/components/front_page/designs/shared'

export default {
  name: 'FrontPageDesignGallery',
  components: { MenuBar },
  data() {
    return {
      logo: BRAND.logo,
      variants: DESIGN_VARIANTS,
    }
  },
}
</script>

<style scoped>
.design-gallery-app {
  background: #f5f5f5;
}

.design-gallery {
  max-width: 1200px;
  margin: 0 auto;
  padding: 32px 24px 80px;
}

.design-gallery__header {
  text-align: center;
  margin-bottom: 48px;
}

.design-gallery__logo {
  height: 48px;
  margin-bottom: 24px;
}

.design-gallery__header h1 {
  font-size: clamp(1.75rem, 3vw, 2.25rem);
  margin: 0 0 16px;
  color: #333;
}

.design-gallery__header p {
  max-width: 640px;
  margin: 0 auto;
  color: #666;
  line-height: 1.6;
}

.design-gallery__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 24px;
}

.design-card {
  text-decoration: none;
  color: inherit;
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  transition: transform 0.2s, box-shadow 0.2s;
}

.design-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(6, 119, 185, 0.15);
}

.design-card__preview {
  height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.design-card__mini-logo {
  height: 32px;
}

.design-card__preview--tauri {
  background: #0f0f0f;
}
.design-card__preview--tauri .design-card__mini-logo {
  filter: brightness(0) invert(1);
}

.design-card__preview--dropbox {
  background: linear-gradient(135deg, #fff 50%, #eef5fa 50%);
}

.design-card__preview--bento {
  background: #fafafa;
  background-image:
    linear-gradient(#e8e8e8 1px, transparent 1px),
    linear-gradient(90deg, #e8e8e8 1px, transparent 1px);
  background-size: 40px 40px;
}

.design-card__preview--nature {
  background: linear-gradient(180deg, #2c3e2a, #4a6741);
}
.design-card__preview--nature .design-card__mini-logo {
  filter: brightness(0) invert(1);
}

.design-card__preview--terminal {
  background: #0d1117;
  font-family: monospace;
}
.design-card__preview--terminal .design-card__mini-logo {
  filter: brightness(0) invert(1);
}

.design-card__preview--split {
  background: linear-gradient(90deg, #f5f5f5 50%, #0677b9 50%);
}
.design-card__preview--split .design-card__mini-logo {
  filter: none;
}

.design-card__preview--glass {
  background: linear-gradient(135deg, rgba(6, 119, 185, 0.6), rgba(123, 172, 214, 0.4));
}
.design-card__preview--glass .design-card__mini-logo {
  filter: brightness(0) invert(1);
}

.design-card__preview--swiss {
  background: #fff;
  border-bottom: 4px solid #111;
}

.design-card__preview--dashboard {
  background: linear-gradient(180deg, #eef5fa, #fff);
}

.design-card__preview--pipeline {
  background: linear-gradient(90deg, #0677b9, #7bacd6, #0677b9);
}
.design-card__preview--pipeline .design-card__mini-logo {
  filter: brightness(0) invert(1);
}

.design-card__body {
  padding: 20px;
}

.design-card__body h2 {
  font-size: 1.15rem;
  margin: 0 0 8px;
  color: #0677b9;
}

.design-card__body p {
  font-size: 0.9rem;
  color: #666;
  margin: 0 0 8px;
  line-height: 1.4;
}

.design-card__inspiration {
  font-size: 0.75rem;
  color: #999;
}

.design-gallery__back {
  text-align: center;
  margin-top: 48px;
}

.design-gallery__back a {
  color: #0677b9;
  text-decoration: none;
}

.design-gallery__back a:hover {
  text-decoration: underline;
}
</style>
